// Composant d'entrée texte moderne pour les formulaires U2000, avec design system unifié
import React from "react";
import U2000Label from "../label/U2000Label";
import type { U2000FormStateHandlers } from "../../../reducer/formReducer/U2000FormStateHandlers";
import { U2000FormStateEnumState } from "../../../reducer/formReducer/U2000FormState";
import U2000InvalidFeedback from "../feedback/U2000InvalidFeedback";
import { u2000SelectInputContentsOnFocus } from "../../../tools/U2000FormInputUtils";

interface U2000InputTextProps {
  formStateHandlers: U2000FormStateHandlers<any>;
  fieldName: string;
  label: string;
  /**
   * Si true, le label n'est pas rendu par le composant.
   * Permet de placer le label manuellement à gauche (ou ailleurs) tout en gardant l'input et le feedback d'erreur.
   * L'input reçoit aria-label avec le texte du label pour l'accessibilité.
   */
  hideLabel?: boolean;
  type?: "text" | "email" | "password" | "tel" | "number";
  readonly?: boolean;
  required?: boolean;
  placeholder?: string;
  maxLength?: number;
  icon?: React.ReactNode;
  onFocus?: (e: React.FocusEvent<HTMLInputElement>) => void;
  onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void;
  inputRef?: React.RefObject<HTMLInputElement>;
  /**
   * Contenu affiché à droite du champ sur la même ligne (ex. case à cocher), aligné verticalement au centre avec l’input.
   * Le message d’erreur reste sous la ligne input + contenu.
   */
  inlineEnd?: React.ReactNode;
  /**
   * Si false, ne sélectionne pas tout le contenu au focus (évite un flash de sélection en grille).
   * Défaut : true (comportement U2000 standard).
   */
  selectOnFocus?: boolean;
  ignoreFormReadState?: boolean;
}

function isInputDisabled(
  formStateHandlers: U2000FormStateHandlers<any>,
  readonly?: boolean,
  ignoreFormReadState?: boolean,
): boolean {
  return readonly || (!ignoreFormReadState && formStateHandlers.getFormState() === U2000FormStateEnumState.READ);
}

// Classes CSS modernes avec design system unifié
const getInputClasses = (
  formStateHandlers: U2000FormStateHandlers<any>,
  fieldName: string,
  isFocused: boolean,
  readonly?: boolean,
  ignoreFormReadState?: boolean,
) => {
  const baseClasses = "w-full px-4 py-3 text-sm border rounded-lg transition-all duration-200 focus:outline-hidden focus:ring-2";
  const isDisabled = isInputDisabled(formStateHandlers, readonly, ignoreFormReadState);
  const hasError = (formStateHandlers.getTouched()[fieldName] && formStateHandlers.getErrors()[fieldName]) || (formStateHandlers.isSubmitted() && formStateHandlers.getErrors()[fieldName]);

  if (isDisabled) {
    return `${baseClasses} bg-muted text-gray-500 border-gray-200 cursor-not-allowed`;
  }

  if (hasError) {
    return `${baseClasses} bg-surface border-red-300 focus:border-red-500 focus:ring-red-500/20 text-gray-900 placeholder:text-gray-400`;
  }

  if (isFocused) {
    return `${baseClasses} bg-surface border-blue-500 ring-blue-500/20 text-gray-900 placeholder:text-gray-400`;
  }

  return `${baseClasses} bg-surface border-gray-300 focus:border-blue-500 focus:ring-blue-500/20 text-gray-900 placeholder:text-gray-400 hover:border-gray-400`;
};

// Utilitaire pour accéder à une propriété imbriquée d'un objet à partir d'un chemin
function getNestedValue<T extends Record<string, any>>(formStateHandlers: U2000FormStateHandlers<T>, fieldName: string): any {
  const data = formStateHandlers.getData() as Record<string, any>;
  return fieldName.split(".").reduce((acc: any, part: string) => (acc !== undefined && acc !== null ? acc[part] : undefined), data);
}

// Composant principal modernisé avec design cohérent
const U2000InputText = <T extends Record<string, any>>({
  formStateHandlers,
  fieldName,
  label,
  hideLabel = false,
  type = "text",
  readonly = false,
  required = false,
  placeholder,
  maxLength,
  icon,
  onFocus,
  onBlur,
  inputRef,
  inlineEnd,
  selectOnFocus = true,
  ignoreFormReadState = false,
}: U2000InputTextProps) => {
  const [isFocused, setIsFocused] = React.useState(false);
  const isDisabled = isInputDisabled(formStateHandlers, readonly, ignoreFormReadState);

  // Détermine si le champ est requis (peut être étendu selon les besoins)
  const isRequired = required;

  // Détermination du type d'input HTML approprié
  const inputType = type === "number" ? "number" : type;

  // Gestion du focus avec état moderne
  function handleFocus(e: React.FocusEvent<HTMLInputElement>) {
    if (selectOnFocus) {
      u2000SelectInputContentsOnFocus(e);
    }
    setIsFocused(true);
    if (onFocus) {
      onFocus(e);
    }
  }

  // Gestion du changement de valeur avec validation de type
  function handleChange(e: React.ChangeEvent<HTMLInputElement>) {
    let newValue: string | number = e.target.value;

    if (type === "number") {
      newValue = e.target.value ? parseFloat(e.target.value) : "";
    }

    if (formStateHandlers.onChange) {
      formStateHandlers.onChange(e, newValue);
    }
  }

  // Gestion du blur avec validation
  function handleBlur(e: React.FocusEvent<HTMLInputElement>) {
    setIsFocused(false);

    if (formStateHandlers.onBlur) {
      formStateHandlers.onBlur(e);
    }

    onBlur && onBlur(e);
  }

  const inputRow = (
    <div className="relative">
      {icon && <div className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 pointer-events-none z-10">{icon}</div>}

      <input
        ref={inputRef}
        type={inputType}
        id={fieldName}
        name={fieldName}
        placeholder={placeholder}
        maxLength={maxLength}
        className={`${getInputClasses(formStateHandlers, fieldName, isFocused, readonly, ignoreFormReadState)} ${icon ? "pl-10" : ""}`}
        value={getNestedValue(formStateHandlers, fieldName) ?? ""}
        onChange={handleChange}
        onFocus={handleFocus}
        onBlur={handleBlur}
        disabled={isDisabled}
        readOnly={readonly}
        required={isRequired}
        aria-label={hideLabel ? label : undefined}
        aria-invalid={!!(formStateHandlers.getTouched()[fieldName] && formStateHandlers.getErrors()[fieldName])}
        aria-describedby={formStateHandlers.getErrors()[fieldName] ? `${fieldName}-error` : undefined}
      />
    </div>
  );

  return (
    <div className="space-y-1.5">
      {!hideLabel && <U2000Label label={label} required={isRequired} htmlFor={fieldName} />}

      {inlineEnd != null ? (
        <div className="flex flex-row items-center gap-4">
          <div className="min-w-0 flex-1">{inputRow}</div>
          <div className="flex shrink-0 items-center *:h-auto *:min-h-0">{inlineEnd}</div>
        </div>
      ) : (
        inputRow
      )}

      {/* Affichage moderne du message d'erreur */}
      <U2000InvalidFeedback<T> formStateHandlers={formStateHandlers} fieldName={fieldName} />
    </div>
  );
};

export default U2000InputText;
